/**
 * PrescreenCriterionCard — one collapsed question row in the prescreen
 * builder's list, laid out like the form builder's QuestionCard: the type
 * badge (form tile names), the question, a choices preview, and the
 * calculation / conditional / eligibility indicators (the rule in the
 * editor's "Not eligible if …" words); edit + delete actions, a drag handle, and
 * ReorderControls (up/down on every viewport, plus top/bottom/jump) — HTML5
 * drag is desktop-only and one-slot-at-a-time, which does not scale to a long
 * prescreen. Editing happens in CriterionEditorModal, not inline.
 */
import { AlertCircle, Calculator, EyeOff, GripVertical, Pencil, Trash2 } from 'lucide-react';
import { DraftCriterion, normalizeShowIf } from '../../types';
import { ruleSummaryText } from '../../lib';
import { CALC_PRESETS, typeLabel } from '../forms/formMeta';
import { excludeSummary, formTypeOf } from './prescreenFormBridge';
import ReorderControls from '../forms/ReorderControls';

/** Inclusion/exclusion chip hues — data colors shared with the published list. */
export const kindChip = (kind: string) =>
  kind === 'exclusion'
    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';

/** Human description of an eligibility rule for the collapsed views (handles
 *  both a single leaf and an AND/OR group via the shared lib formatter). */
export function ruleSummary(rule: any, answerType: string): string {
  const s = ruleSummaryText(rule, answerType);
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Recruiter judgment';
}

interface PrescreenCriterionCardProps {
  criterion: DraftCriterion;
  /** 1-based display number (also its position for the move controls). */
  number: number;
  /** Total criteria in the list. */
  total: number;
  onEdit: () => void;
  onDelete: () => void;
  /** Move this criterion to a 1-based position. */
  onMoveTo: (position: number) => void;
}

export default function PrescreenCriterionCard({
  criterion: c, number, total, onEdit, onDelete, onMoveTo,
}: PrescreenCriterionCardProps) {
  const isCalc = !!c.calculation;
  const options = c.options || [];
  const formType = formTypeOf(c);
  // The rule in the editor's "Not eligible if …" words when it has that form;
  // otherwise its passing condition. A calc's rule judges the COMPUTED value.
  const hasRule = ruleSummaryText(c.rule, isCalc ? 'number' : c.answer_type) !== '';
  const summary = hasRule
    ? excludeSummary(c.rule, formType, options) || ruleSummary(c.rule, isCalc ? 'number' : c.answer_type)
    : null;
  const showIf = normalizeShowIf(c.show_if);

  return (
    <div className="group flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-shadow duration-150 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 sm:p-4">
      {/* Drag handle (desktop) */}
      <div className="hidden cursor-grab pt-0.5 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 sm:block" title="Drag to reorder">
        <GripVertical className="h-5 w-5" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-400">Q{number}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            {typeLabel(formType)}
          </span>
        </div>

        <p className="mt-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {c.display_question || <span className="italic text-gray-400">(untitled question)</span>}
        </p>

        {options.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {options.slice(0, 4).map((o, i) => (
              <span key={i} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {o.label || `Option ${i + 1}`}
              </span>
            ))}
            {options.length > 4 && (
              <span className="text-xs text-gray-400">+{options.length - 4} more</span>
            )}
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-3">
          {isCalc && (
            <span className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
              <Calculator className="h-3 w-3" /> {CALC_PRESETS[c.calculation!.preset]?.label || c.calculation!.preset}, never asked
            </span>
          )}
          {showIf && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <EyeOff className="h-3 w-3" />
              {showIf.conditions.length > 1
                ? `Conditional (${showIf.match} of ${showIf.conditions.length})`
                : 'Conditional'}
            </span>
          )}
          {summary && (
            <span className="inline-flex min-w-0 items-center gap-1 text-xs text-purple-600 dark:text-purple-400">
              <AlertCircle className="h-3 w-3 shrink-0" /> <span className="truncate">{summary}</span>
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-start gap-0.5">
        <ReorderControls position={number} total={total} onMoveTo={onMoveTo} what="question" />
        <button
          onClick={onEdit}
          className="rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          aria-label="Edit question"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
          aria-label="Delete question"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
